import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import styled, { css } from 'styled-components';
import escapeStringRegexp from 'escape-string-regexp';

import Input from '../../Input';
// TODO: just as a quick fix for old boneyard hosted components
import oldTheme from '../../Colors';

// Retrieve unstyled components for determining child type
import { OptionList } from '../OptionList';
import { SingleSelectList } from '../SingleSelectList/SingleSelectList';

export const TypeAhead = styled.div`
  display: block;
  cursor: pointer;
  position: relative; /* z-index hack */
  padding: 0;
  margin-bottom: 0;
`;

export const TypeAheadInput = styled(Input)`
  width: 100%;
  color: ${props => oldTheme.colors.modern.textFilter.placeholder};
  font-size: 1.25rem;
  border: none;
  padding: 0.5rem;
  padding-left: 1rem;
  box-shadow: inset -1px 0 0 0 ${props => oldTheme.colors.modern.textFilter.border},
    inset 1px 0 0 0 ${props => oldTheme.colors.modern.textFilter.border},
    inset 0 1px 0 0 ${props => oldTheme.colors.modern.textFilter.border},
    inset 0 5px 10px 0 ${props => oldTheme.colors.modern.textFilter.shadow},
    0 1px 0 0 ${props => oldTheme.colors.modern.textFilter.border};

  ${props =>
    props.hasChanged &&
    css`
      color: ${oldTheme.colors.modern.textFilter.text};
      font-weight: bold;
    `};
`;

export const WithFilter = styled.div`
  display: flex;
  flex: 1;
`;

export const List = styled.ul`
  margin-top: 0;
`;

export const NoResults = styled.div`
  padding: 0.5rem;
`;

/** @visibleName Text Filter */
export class TextFilter extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      filterValue: props.filterValue,
    };

    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.getOptions = this.getOptions.bind(this);
    this.generateFilterWords = this.generateFilterWords.bind(this);
    this.toRef = this.toRef.bind(this);
  }

  static propTypes = {
    compareItemToFilter: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    filterValue: PropTypes.string,
    value: PropTypes.string,
    image: PropTypes.node,
    automationHook: PropTypes.string,
    children: function(props, propName, componentName) {
      var prop = props[propName];
      var types = [OptionList, SingleSelectList];
      // `target` references an unstyled comment from a styled component
      var type = prop && (prop.type.target || prop.type);
      // Only accept a single child, of the appropriate component type
      if (React.Children.count(prop) !== 1 || types.indexOf(type) === -1) {
        return new Error(
          '`' +
            componentName +
            '` ' +
            'should have a single child of the following types: ' +
            ' `' +
            types.map(fn => fn.name).join('`, `') +
            '` but received `' +
            type +
            '`.'
        );
      }
    },
    resultNotFound: PropTypes.string,
    /** injects the generated stylesheet */
    className: PropTypes.string,
    /** focus event handler passes a reference of this component to its consumer */
    onFocus: PropTypes.func,
    /** function callback when the filter text changes*/
    onFilterChange: PropTypes.func,
  };

  static defaultProps = {
    placeholder: 'Type here...',
    filterValue: '',
    onFocus: Function.prototype,
    onFilterChange: Function.prototype,
  };

  handleFilterChange(value) {
    if (this.props.value) {
      this.props.onFilterChange();
    }
    this.setState({
      filterValue: value,
    });
  }

  generateFilterWords() {
    var escapedSearchValue =
      this.state.filterValue && escapeStringRegexp(this.state.filterValue);
    // Split the search value into words and eliminate all empty strings
    return escapedSearchValue.split(/(\s+)/).filter(word => word.trim());
  }

  getOptions() {
    if (!this.props.children) {
      return [];
    }

    return this.props.children.props.children.filter(option => {
      return this.props.compareItemToFilter(
        option.props.item,
        this.generateFilterWords(),
        this.state.filterValue
      );
    });
  }

  toRef(el) {
    this.el = el;
  }

  renderList() {
    let list = this.props.children;

    if (this.getOptions().length === 0) {
      return null;
    }

    const ListEl = List.withComponent(list.type);

    return (
      <WithFilter ref={this.toRef}>
        <ListEl {...list.props}>{this.getOptions()}</ListEl>
      </WithFilter>
    );
  }

  render() {
    return (
      <div className={this.props.className}>
        <TypeAhead>
          <TypeAheadInput
            type="text"
            name="filter"
            autoComplete="off"
            placeholder={this.props.placeholder}
            hasChanged={this.props.placeholder !== this.state.filterValue}
            image={this.props.image}
            value={this.props.value ? this.props.value : this.state.filterValue}
            onFocus={this.props.onFocus}
            onChange={this.handleFilterChange}
            automationHook={this.props.automationHook}
          />
        </TypeAhead>
        {this.renderList()}
      </div>
    );
  }
}

export default styled(TextFilter)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 100px;
  margin: ${props => props.theme.spacing(2)};
`;
